import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Edit,
  Trash2,
  Calendar as CalendarIcon,
  Home,
  User,
  DollarSign,
  Briefcase,
  ClipboardList,
  Lock,
} from 'lucide-react';
import {
  deleteBooking,
  deleteCleaningTask,
  deleteMaintenanceTask,
} from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const TYPE_LABELS = {
  booking: 'Booking',
  cleaning: 'Cleaning Task',
  maintenance: 'Maintenance Task',
  landscaping: 'Landscaping Task',
  renovation: 'Renovation Project',
  owner_use: 'Owner Use',
};

const TYPE_COLORS = {
  booking: 'bg-blue-100 text-blue-800',
  cleaning: 'bg-emerald-100 text-emerald-800',
  maintenance: 'bg-amber-100 text-amber-800',
  landscaping: 'bg-green-100 text-green-800',
  renovation: 'bg-violet-100 text-violet-800',
  owner_use: 'bg-rose-100 text-rose-800',
};

const formatDate = (d) => {
  if (!d) return '—';
  try {
    return format(parseISO(d), 'PPP');
  } catch {
    return d;
  }
};

export const EventDetailsDialog = ({
  open,
  onClose,
  event,
  properties,
  onEdit,
}) => {
  if (!event) return null;

  const restricted = event.details?._restricted === true;
  const propertyName =
    properties?.find((p) => p.id === event.property_id)?.name || 'Unknown property';

  const handleDelete = async () => {
    const confirmMsg = `Delete this ${TYPE_LABELS[event.type] || 'event'}?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      if (event.type === 'booking' || event.type === 'owner_use') {
        await deleteBooking(event.id);
      } else if (event.type === 'cleaning') {
        await deleteCleaningTask(event.id);
      } else if (event.type === 'maintenance' || event.type === 'landscaping') {
        await deleteMaintenanceTask(event.id);
      } else if (event.type === 'renovation') {
        await api.delete(`/renovation-projects/${event.id}`);
      }
      toast.success('Deleted successfully');
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="sm:max-w-[480px]" data-testid="event-details-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle className="font-manrope">{event.title}</DialogTitle>
          <DialogDescription className="text-slate-500">Details for this calendar event.</DialogDescription>
            <Badge className={TYPE_COLORS[event.type] || 'bg-slate-100 text-slate-700'}>
              {TYPE_LABELS[event.type] || event.type}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="flex items-start gap-2">
            <Home className="h-4 w-4 text-slate-400 mt-0.5" />
            <div>
              <div className="text-slate-500">Property</div>
              <div className="font-medium text-slate-900">{propertyName}</div>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <CalendarIcon className="h-4 w-4 text-slate-400 mt-0.5" />
            <div>
              <div className="text-slate-500">
                {event.end_date ? 'Dates' : 'Scheduled Date'}
              </div>
              <div className="font-medium text-slate-900">
                {formatDate(event.start_date)}
                {event.end_date ? ` → ${formatDate(event.end_date)}` : ''}
              </div>
            </div>
          </div>

          {event.status && (
            <div className="flex items-start gap-2">
              <ClipboardList className="h-4 w-4 text-slate-400 mt-0.5" />
              <div>
                <div className="text-slate-500">Status</div>
                <div className="font-medium text-slate-900 capitalize">
                  {String(event.status).replace(/_/g, ' ')}
                </div>
              </div>
            </div>
          )}

          {event.details?.guest_id && event.type === 'booking' && (
            <div className="flex items-start gap-2">
              <User className="h-4 w-4 text-slate-400 mt-0.5" />
              <div>
                <div className="text-slate-500">Guest</div>
                <div className="font-medium text-slate-900">{event.title}</div>
              </div>
            </div>
          )}

          {event.details?.total_price > 0 && (
            <div className="flex items-start gap-2">
              <DollarSign className="h-4 w-4 text-slate-400 mt-0.5" />
              <div>
                <div className="text-slate-500">Total Price</div>
                <div className="font-medium text-slate-900">
                  ${event.details.total_price.toLocaleString()}
                </div>
              </div>
            </div>
          )}

          {event.details?.estimated_budget > 0 && (
            <div className="flex items-start gap-2">
              <DollarSign className="h-4 w-4 text-slate-400 mt-0.5" />
              <div>
                <div className="text-slate-500">Est. Budget</div>
                <div className="font-medium text-slate-900">
                  ${event.details.estimated_budget.toLocaleString()}
                </div>
              </div>
            </div>
          )}

          {event.details?.contractor_name && (
            <div className="flex items-start gap-2">
              <Briefcase className="h-4 w-4 text-slate-400 mt-0.5" />
              <div>
                <div className="text-slate-500">Contractor</div>
                <div className="font-medium text-slate-900">
                  {event.details.contractor_name}
                </div>
              </div>
            </div>
          )}

          {event.details?.notes && (
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-slate-500 text-xs mb-1">Notes</div>
              <div className="text-slate-800">{event.details.notes}</div>
            </div>
          )}

          {event.details?.description && (
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-slate-500 text-xs mb-1">Description</div>
              <div className="text-slate-800">{event.details.description}</div>
            </div>
          )}

          {restricted && (
            <div
              className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3"
              data-testid="event-details-restricted"
            >
              <Lock className="h-4 w-4 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-900">
                This task hasn&apos;t been shared with you. Ask a manager to add you
                as a collaborator to see full details or update it.
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex !justify-between gap-2 pt-4">
          <Button
            variant="outline"
            onClick={handleDelete}
            disabled={restricted}
            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            data-testid="event-details-delete-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onClose(false)}>
              Close
            </Button>
            {onEdit && !restricted && (
              <Button
                onClick={() => onEdit(event)}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="event-details-edit-btn"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EventDetailsDialog;
